<template>
  <div 
    class="performance-optimized-container bg-white/90 rounded-xl p-6 md:p-8 shadow-lg border border-gray-100"
    v-memo="[totalRemaining, progressPercentage, hasRemainingDucks]"
  >
    <div class="max-w-4xl mx-auto">
      
      <!-- Section Header -->
      <div class="flex items-center justify-center mb-6">
        <div class="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center mr-4 flex-shrink-0 shadow-md">
          <v-icon color="white" size="24">mdi-duck</v-icon>
        </div>
        <div class="text-center">
          <h2 class="text-2xl md:text-3xl font-bold text-slate-800">Duck Hunt Progress</h2>
          <div class="h-1 w-32 bg-amber-500 rounded-full mt-2 mx-auto"></div>
        </div>
      </div>
      
      <!-- Overall Progress Statistics -->
      <div class="mb-8">
        <div class="text-center mb-6">
          <p class="text-gray-600 mb-4 leading-relaxed">
            Track your duck hunting progress and discover what treasures still await!
          </p>
          
          <!-- Progress Stats -->
          <div class="flex justify-center items-center gap-4 mb-6 flex-wrap">
            <div class="stat-card bg-white px-4 py-3 rounded-lg shadow-sm border border-gray-200">
              <p class="text-xs text-gray-500 uppercase tracking-wide">Total</p>
              <p class="font-bold text-xl text-slate-800">{{ getTotalDucks }}</p>
            </div>
            <div class="stat-card bg-green-50 px-4 py-3 rounded-lg shadow-sm border border-green-200">
              <p class="text-xs text-gray-500 uppercase tracking-wide">Found</p>
              <p class="font-bold text-xl text-green-600">{{ getFoundDucks }}</p>
            </div>
            <div class="stat-card bg-amber-50 px-4 py-3 rounded-lg shadow-sm border border-amber-200">
              <p class="text-xs text-gray-500 uppercase tracking-wide">Remaining</p>
              <p class="font-bold text-xl text-amber-600">{{ totalRemaining }}</p>
            </div>
          </div>
          
          <!-- Progress Bar -->
          <div class="progress-container w-full max-w-md mx-auto bg-gray-200 h-4 rounded-full overflow-hidden shadow-inner mb-4">
            <div 
              class="progress-bar h-full bg-amber-500 transition-all duration-500 ease-out" 
              :style="{ width: progressPercentageWidth }"
            ></div>
          </div>
          
          <p class="text-sm text-gray-600">
            {{ progressPercentage }}% Complete • {{ getFoundDucks }} duck{{ getFoundDucks === 1 ? '' : 's' }} found
          </p>
        </div>
      </div>
      
      <!-- Rarity Breakdown Section -->
      <div class="mb-8">
        <h3 class="text-xl font-bold text-slate-800 mb-6 text-center">Duck Collection Progress by Rarity</h3>
        
        <!-- Rarity Cards -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
          <!-- Common Ducks -->
          <div v-if="totalDucksByRarity.COMMON > 0" class="rarity-card bg-green-50 border border-green-200">
            <div class="text-center mb-4">
              <img src="/duckRarity/COMMON.webp" alt="Common Duck" class="w-16 h-16 mx-auto rounded-xl shadow-md mb-3" />
              <div class="font-bold text-lg text-green-800">Common Ducks</div>
              <div class="text-sm text-green-600 mb-2">Easy to spot in popular areas</div>
              <div class="text-3xl font-bold text-green-700">
                {{ foundDucksByRarity.COMMON }}/{{ totalDucksByRarity.COMMON }}
              </div>
              <div class="text-xs text-green-600">
                {{ remainingDucksByRarity.COMMON > 0 ? `${remainingDucksByRarity.COMMON} remaining` : 'All found!' }}
              </div>
            </div>
            
            <div class="mb-4">
              <div class="w-full bg-green-200 rounded-full h-3 mb-2">
                <div 
                  class="bg-green-500 h-3 rounded-full transition-all duration-300 ease-out"
                  :style="{ width: getProgressWidth('COMMON') + '%' }"
                ></div>
              </div>
              <div class="text-xs text-center text-green-600">{{ getProgressWidth('COMMON') }}% found</div>
            </div>
            
            <div class="bg-white/60 rounded-lg p-3">
              <div class="text-xs text-green-700 font-medium mb-1">💡 Hunting Tips:</div>
              <div class="text-xs text-green-600">Check lounges, deck areas, and common gathering spots</div>
            </div>
          </div>
          
          <!-- Rare Ducks -->
          <div v-if="totalDucksByRarity.RARE > 0" class="rarity-card bg-blue-50 border border-blue-200">
            <div class="text-center mb-4">
              <img src="/duckRarity/RARE.webp" alt="Rare Duck" class="w-16 h-16 mx-auto rounded-xl shadow-md mb-3" />
              <div class="font-bold text-lg text-blue-800">Rare Ducks</div>
              <div class="text-sm text-blue-600 mb-2">Hidden in clever spots</div>
              <div class="text-3xl font-bold text-blue-700">
                {{ foundDucksByRarity.RARE }}/{{ totalDucksByRarity.RARE }}
              </div>
              <div class="text-xs text-blue-600">
                {{ remainingDucksByRarity.RARE > 0 ? `${remainingDucksByRarity.RARE} remaining` : 'All found!' }}
              </div>
            </div>
            
            <div class="mb-4">
              <div class="w-full bg-blue-200 rounded-full h-3 mb-2">
                <div 
                  class="bg-blue-500 h-3 rounded-full transition-all duration-300 ease-out"
                  :style="{ width: getProgressWidth('RARE') + '%' }"
                ></div>
              </div>
              <div class="text-xs text-center text-blue-600">{{ getProgressWidth('RARE') }}% found</div>
            </div>
            
            <div class="bg-white/60 rounded-lg p-3">
              <div class="text-xs text-blue-700 font-medium mb-1">💡 Hunting Tips:</div>
              <div class="text-xs text-blue-600">Look behind decorations, near artwork, and quieter corners</div>
            </div>
          </div>
          
          <!-- Legendary Ducks -->
          <div v-if="totalDucksByRarity.LEGENDARY > 0" class="rarity-card bg-purple-50 border border-purple-200">
            <div class="text-center mb-4">
              <img src="/duckRarity/LEGENDARY.webp" alt="Legendary Duck" class="w-16 h-16 mx-auto rounded-xl shadow-md mb-3" />
              <div class="font-bold text-lg text-purple-800">Legendary Ducks</div>
              <div class="text-sm text-purple-600 mb-2">Ultimate challenge!</div>
              <div class="text-3xl font-bold text-purple-700">
                {{ foundDucksByRarity.LEGENDARY }}/{{ totalDucksByRarity.LEGENDARY }}
              </div>
              <div class="text-xs text-purple-600">
                {{ remainingDucksByRarity.LEGENDARY > 0 ? `${remainingDucksByRarity.LEGENDARY} remaining` : 'All found!' }}
              </div>
            </div>
            
            <div class="mb-4">
              <div class="w-full bg-purple-200 rounded-full h-3 mb-2">
                <div 
                  class="bg-purple-500 h-3 rounded-full transition-all duration-300 ease-out"
                  :style="{ width: getProgressWidth('LEGENDARY') + '%' }"
                ></div>
              </div>
              <div class="text-xs text-center text-purple-600">{{ getProgressWidth('LEGENDARY') }}% found</div>
            </div>
            
            <div class="bg-white/60 rounded-lg p-3">
              <div class="text-xs text-purple-700 font-medium mb-1">💡 Hunting Tips:</div>
              <div class="text-xs text-purple-600">Explore unique spots, hidden nooks, and unexpected places</div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Encouragement Message -->
      <div class="text-center bg-amber-50 rounded-lg p-6 border border-amber-200">
        <v-icon color="amber-darken-2" size="32" class="mb-3">mdi-treasure-chest</v-icon>
        <div class="text-lg font-medium text-amber-800 mb-2">
          {{ totalRemaining > 0 ? 'Adventure Continues!' : 'Collection Complete!' }}
        </div>
        <div class="text-sm text-amber-600">
          {{ totalRemaining > 0 
            ? `${totalRemaining} more duck${totalRemaining === 1 ? '' : 's'} waiting to be discovered. Keep exploring!` 
            : 'Congratulations! You\'ve found all the ducks on this cruise!' 
          }}
        </div>
        <div v-if="totalRemaining > 0" class="text-xs text-amber-500 mt-2">
          New ducks may be hidden throughout your journey - check back often!
        </div>
      </div>
      
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useDemoDuckStore } from '@/stores/demoDuckStore';
import type { RemainingDucksByRarity } from '@/interfaces/cruise';

const props = defineProps<{
  remainingDucksByRarity: RemainingDucksByRarity;
}>();

// Demo duck store access for progress data
const demoDuckStore = useDemoDuckStore();
const { ducks } = storeToRefs(demoDuckStore);

// Get total and found ducks for demo
const getTotalDucks = computed(() => ducks.value.length);
const getFoundDucks = computed(() => ducks.value.filter(duck => duck.found === 1).length);

// Get found ducks by rarity
const foundDucksByRarity = computed(() => {
  const found = { COMMON: 0, RARE: 0, LEGENDARY: 0 };
  ducks.value.forEach(duck => {
    if (duck.found === 1 && duck.rarity) {
      found[duck.rarity]++;
    }
  });
  return found;
});

// Get total ducks by rarity (found + remaining)
const totalDucksByRarity = computed(() => {
  const { COMMON, RARE, LEGENDARY } = props.remainingDucksByRarity;
  const found = foundDucksByRarity.value;
  return {
    COMMON: found.COMMON + COMMON,
    RARE: found.RARE + RARE,
    LEGENDARY: found.LEGENDARY + LEGENDARY
  };
});

// Memoized computed properties for better performance
const hasRemainingDucks = computed(() => {
  const { COMMON, RARE, LEGENDARY } = props.remainingDucksByRarity;
  return COMMON > 0 || RARE > 0 || LEGENDARY > 0;
});

const totalRemaining = computed(() => {
  const { COMMON, RARE, LEGENDARY } = props.remainingDucksByRarity;
  return COMMON + RARE + LEGENDARY;
});

// Progress percentage for overall progress bar
const progressPercentage = computed(() => {
  const total = getTotalDucks.value;
  if (total === 0) return 0;
  return Math.round((getFoundDucks.value / total) * 100);
});

// Cached progress percentage width string to avoid repeated string interpolation
const progressPercentageWidth = computed(() => `${progressPercentage.value}%`);

// Optimized progress width calculation with caching - now shows found vs total
const progressWidthCache = new Map<string, number>();

const getProgressWidth = (rarity: keyof RemainingDucksByRarity): number => {
  const found = foundDucksByRarity.value[rarity];
  const total = totalDucksByRarity.value[rarity];
  const cacheKey = `${rarity}-${found}-${total}`;
  
  if (progressWidthCache.has(cacheKey)) {
    return progressWidthCache.get(cacheKey)!;
  }
  
  // Calculate percentage of found ducks for this rarity
  const width = total > 0 ? Math.round((found / total) * 100) : 0;
  progressWidthCache.set(cacheKey, width);
  
  return width;
};
</script>

<style scoped>
/* Performance-optimized styles */
.performance-optimized-container {
  /* Use a simple fade-in animation instead of complex Motion */
  animation: optimizedFadeIn 0.4s ease-out;
}

.stat-card {
  /* Simplified transition for better performance */
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  /* Remove complex animations on load */
}

.stat-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.progress-bar {
  /* Remove expensive pseudo-element animations */
  position: relative;
  border-radius: inherit;
}

.rarity-card {
  padding: 1.5rem;
  border-radius: 1rem;
  /* Minimal transition for better performance */
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.rarity-card:hover {
  /* Reduce scale and movement for better performance */
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
}

/* Simple fade-in animation */
@keyframes optimizedFadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .rarity-card {
    padding: 1rem;
  }
  
  .stat-card {
    padding: 0.75rem;
  }
}

/* Performance: Disable animations on reduced motion and low-end devices */
@media (prefers-reduced-motion: reduce) {
  .performance-optimized-container,
  .rarity-card,
  .stat-card,
  .progress-bar {
    animation: none !important;
    transition: none !important;
  }
  
  .rarity-card:hover,
  .stat-card:hover {
    transform: none !important;
  }
}

/* Additional performance optimizations */
.rarity-card img {
  /* Optimize image rendering */
  image-rendering: optimizeQuality;
  image-rendering: -webkit-optimize-contrast;
}

/* Use GPU acceleration sparingly and only for necessary elements */
.stat-card,
.rarity-card {
  will-change: auto; /* Let browser decide when to use GPU acceleration */
}

/* Force hardware acceleration only on hover for better performance */
.stat-card:hover,
.rarity-card:hover {
  will-change: transform;
}

.stat-card:not(:hover),
.rarity-card:not(:hover) {
  will-change: auto;
}
</style>